import { Marked, Token } from 'marked';
import markedKatex from 'marked-katex-extension';
import {
    AlignmentType,
    BorderStyle,
    Document,
    Footer,
    Header,
    HeadingLevel,
    ImageRun,
    PageNumber,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    TextRun,
    WidthType,
} from 'docx';
import {
    DEFAULT_CONTENT_CONFIG,
    TTSConfig,
    DEFAULT_TTS_CONFIG,
    EntityProcessor,
    preProcessMarkdown,
    postProcessText,
} from './textProcessing';
import { ExportContentConfig } from '../types/models';
import { closeIncompleteLatex, preprocessLaTeX } from './latexProcessing';
import { ttsMarked } from './marked/ttsRenderer';
import { htmlExportMarked } from './marked/htmlExportRenderer';
import { plainTextMarked } from './marked/plainTextRenderer';
import { markedTexMath } from './marked/texMathExtension';

const contentLexer = new Marked({ gfm: true });
contentLexer.use(markedKatex({ throwOnError: false }));
contentLexer.use(markedTexMath());

/**
 * Lightweight configuration bridge for content vs TTS pipelines.
 * Why: Export flows share parsing but diverge in rendering needs; this keeps
 * knobs separated while allowing shared preprocessing.
 */
interface ProcessingOptions {
    content?: Partial<ExportContentConfig>;
    tts?: Partial<TTSConfig>;
}

export type DocxProfile = 'generic' | 'memo';

export interface DocxGenerationOptions {
    profile?: DocxProfile;
    includeHeader?: boolean;
    includeFooter?: boolean;
    title?: string;
    headerText?: string;
    footerText?: string;
    branding?: {
        enabled?: boolean;
        accentColorHex?: string;
        accentSecondaryColorHex?: string;
        /**
         * PNG bytes for a small corner logo placed in the DOCX header.
         * Why: keeps branding subtle without affecting body layout.
         * Uses Uint8Array so this type is not Node-specific (Buffer extends Uint8Array).
         */
        logo?: {
            pngData: Uint8Array;
            /** Render size in pixels (docx ImageRun transformation uses px). */
            renderPx?: number;
        };
        /**
         * Minimal styling mode for the header/title area.
         * - titleRule: add a thin accent rule under the title/header block (default)
         * - headingColor: apply accent color to headings
         */
        style?: 'titleRule' | 'headingColor';
    };
}

export class ContentProcessor {
    private readonly tokens: Token[] = [];
    // Accepts a union config but stores individually

    private readonly contentConfig: Partial<ExportContentConfig> = {};

    private readonly ttsConfig: Partial<TTSConfig> = {};

    constructor(markdownText: string, options: ProcessingOptions = {}) {
        // Apply defaults and merge overrides for each pipeline
        this.contentConfig = { ...DEFAULT_CONTENT_CONFIG, ...options.content };
        this.ttsConfig = { ...DEFAULT_TTS_CONFIG, ...options.tts };

        // Clean HTML entities to avoid double-encoding across renderers
        const cleanText = EntityProcessor.decode(markdownText);

        // Close any unclosed math delimiters (truncated LLM output), then normalise
        const latexClosed = closeIncompleteLatex(cleanText);
        const latexClean = preprocessLaTeX(latexClosed);

        // Apply TTS preprocessing; harmless for non-TTS flows and keeps one logical path
        const preprocessedText = preProcessMarkdown(latexClean, this.ttsConfig as TTSConfig);

        this.tokens = contentLexer.lexer(preprocessedText);

        // Handle marked lexer's entity encoding in codespan tokens
        this.cleanCodespanTokens(this.tokens);
    }

    // Marked's lexer introduces entities in code content that need cleanup.
    private cleanCodespanTokens(tokens: any[]): void {
        tokens.forEach(token => {
            if (token.type === 'codespan' && token.text) {
                token.text = EntityProcessor.decode(token.text);
            } else if (token.tokens) {
                this.cleanCodespanTokens(token.tokens);
            } else if (token.items) {
                token.items.forEach((item: any) => {
                    if (item.tokens) {
                        this.cleanCodespanTokens(item.tokens);
                    }
                });
            }
        });
    }

    /**
     * Returns true when the parsed token stream contains any math tokens
     * (blockKatex / inlineKatex from marked-katex-extension or texMathExtension).
     * Used by the export pipeline to conditionally include heavy KaTeX CSS + fonts.
     */
    hasMath(): boolean {
        return ContentProcessor.tokenTreeHasMath(this.tokens);
    }

    private static tokenTreeHasMath(tokens: any[]): boolean {
        for (const token of tokens) {
            if (token.type === 'blockKatex' || token.type === 'inlineKatex') return true;
            if (Array.isArray(token.tokens) && ContentProcessor.tokenTreeHasMath(token.tokens)) return true;
            if (Array.isArray(token.items)) {
                for (const item of token.items) {
                    if (Array.isArray(item.tokens) && ContentProcessor.tokenTreeHasMath(item.tokens)) return true;
                }
            }
        }
        return false;
    }

    // Generate speech-friendly text from markdown; reuse as plain text baseline.
    // TODO : This isn't great; consider a dedicated plaintext generator that reconstructs from tokens rather than relying on the TTS renderer, which is optimized for natural language flow rather than structured readability (md gang)
    generateTtsText(): string {
        const parsed = ttsMarked.parser(this.tokens) as string;
        return postProcessText(parsed, this.ttsConfig as TTSConfig);
    }

    // Use HTML export renderer with proper escaping for PDF HTML pipeline.
    generatePdfHtml(): string {
        return htmlExportMarked.parser(this.tokens) as string;
    }

    // Generate markdown while reconstructing core structures from tokens.
    generateMarkdown(): string {
        const preprocessedText = this.tokens
            .map((token) => {
                // Skip whitespace-only tokens — each handler controls its own spacing.
                if (token.type === 'space') return '';
                if (token.type === 'heading') {
                    return `${'#'.repeat(token.depth)} ${token.text}\n\n`;
                }
                if (token.type === 'paragraph') {
                    return `${token.text}\n\n`;
                }
                if (token.type === 'code') {
                    return `\`\`\`${token.lang || ''}\n${token.text}\n\`\`\`\n\n`;
                }
                if (token.type === 'list') {
                    return ContentProcessor.reconstructList(token) + '\n';
                }
                // Block math token added by marked-katex-extension — preserve raw delimiters
                if (token.type === 'blockKatex') {
                    return `$$\n${(token as any).text}\n$$\n\n`;
                }
                return token.raw || '';
            })
            .join('');

        return preprocessedText.trim();
    }

    /**
     * Build a docx Document from tokenised markdown.
     *
     * Why: DOCX is binary and structurally incompatible with the string-based
     * render pipeline. We convert at the boundary: markdown tokens → docx paragraphs.
     *
     * Profiles control typography/layout presets:
     * - generic: Roboto font, modern spacing, optional branding accent
     * - memo: Times New Roman, MIL-ish spacing, page numbering from page 2
     */
    generateDocx(prebuiltChildren?: (Paragraph | Table)[], options: DocxGenerationOptions = {}): Document {
        const profile: DocxProfile = options.profile ?? 'generic';
        const includeHeader = options.includeHeader ?? true;
        const includeFooter = options.includeFooter ?? true;
        const brandingEnabled = options.branding?.enabled ?? false;
        const accentColorHex = (options.branding?.accentColorHex ?? '#811411')
            .replace('#', '')
            .toUpperCase();
        const accentSecondaryColorHex = (
            options.branding?.accentSecondaryColorHex ?? '#962424'
        )
            .replace('#', '')
            .toUpperCase();
        const brandingStyle = options.branding?.style ?? 'titleRule';

        const normalFont = profile === 'memo' ? 'Times New Roman' : 'Roboto';
        const normalSizeHalfPoints = profile === 'memo' ? 24 : 22; // 12pt vs 11pt

        // 1 inch margins in twips (1440 = 72pt = 1 inch)
        const margins = { top: 1440, right: 1440, bottom: 1440, left: 1440 };

        // MIL-ish spacing: single within, double between paragraphs (handled via "after").
        // For generic, keep a slightly tighter modern feel.
        const paragraphAfter = profile === 'memo' ? 240 : 160;

        const headerParagraphs: Paragraph[] = [];
        if (includeHeader) {
            if (options.title && options.title.trim()) {
                const titleText = options.title.trim();
                headerParagraphs.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_1,
                        children:
                            brandingEnabled && brandingStyle === 'headingColor'
                                ? [
                                      new TextRun({
                                          text: titleText,
                                          color: accentColorHex,
                                      }),
                                  ]
                                : [new TextRun({ text: titleText })],
                    }),
                );
            }
            if (options.headerText && options.headerText.trim()) {
                const headerText = options.headerText.trim();
                headerParagraphs.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: headerText,
                                italics: true,
                                size: Math.max(18, normalSizeHalfPoints - 2),
                                ...(brandingEnabled
                                    ? { color: accentSecondaryColorHex }
                                    : {}),
                            }),
                        ],
                        spacing: { after: paragraphAfter },
                    }),
                );
            }

            // Subtle branding: thin accent rule under the header/title block.
            // Why: avoids coloring body text while still providing a branded cue.
            if (brandingEnabled && brandingStyle === 'titleRule') {
                headerParagraphs.push(
                    new Paragraph({
                        children: [new TextRun({ text: '' })],
                        border: {
                            bottom: {
                                color: accentColorHex,
                                style: BorderStyle.SINGLE,
                                size: 6,
                                space: 1,
                            },
                        },
                        spacing: { after: paragraphAfter },
                    }),
                );
            }
        }

        const children: (Paragraph | Table)[] = prebuiltChildren ?? this.buildDocxParagraphs(options);

        const footer =
            includeFooter &&
            (options.footerText?.trim() || profile === 'memo')
                ? new Footer({
                      children: [
                          ...(profile === 'generic' && options.footerText?.trim()
                              ? [
                                    new Paragraph({
                                        text: options.footerText.trim(),
                                        alignment: AlignmentType.LEFT,
                                    }),
                                ]
                              : []),
                          new Paragraph({
                              children: [
                                  new TextRun({
                                      children: [PageNumber.CURRENT],
                                  }),
                              ],
                              alignment: AlignmentType.CENTER,
                          }),
                      ],
                  })
                : undefined;

        const header =
            includeHeader &&
            brandingEnabled &&
            options.branding?.logo?.pngData
                ? new Header({
                      children: [
                          new Paragraph({
                              alignment: AlignmentType.RIGHT,
                              children: [
                                  new ImageRun({
                                      type: 'png',
                                      data: options.branding.logo.pngData,
                                      transformation: {
                                          width:
                                              options.branding.logo.renderPx ??
                                              48,
                                          height:
                                              options.branding.logo.renderPx ??
                                              48,
                                      },
                                  }),
                              ],
                          }),
                      ],
                  })
                : undefined;

        return new Document({
            styles: {
                default: {
                    document: {
                        run: {
                            font: normalFont,
                            size: normalSizeHalfPoints,
                        },
                        paragraph: {
                            spacing: {
                                after: paragraphAfter,
                            },
                        },
                    },
                },
                ...(brandingEnabled
                    ? {
                          paragraphStyles: [
                              {
                                  id: 'Heading1',
                                  name: 'Heading 1',
                                  basedOn: 'Normal',
                                  next: 'Normal',
                                  quickFormat: true,
                                  run: {
                                      color: accentColorHex,
                                      bold: true,
                                  },
                              },
                              {
                                  id: 'Heading2',
                                  name: 'Heading 2',
                                  basedOn: 'Normal',
                                  next: 'Normal',
                                  quickFormat: true,
                                  run: {
                                      color: accentSecondaryColorHex,
                                      bold: true,
                                  },
                              },
                              {
                                  id: 'Heading3',
                                  name: 'Heading 3',
                                  basedOn: 'Normal',
                                  next: 'Normal',
                                  quickFormat: true,
                                  run: {
                                      color: accentSecondaryColorHex,
                                      bold: true,
                                  },
                              },
                          ],
                      }
                    : {}),
            },
            sections: [
                {
                    properties: {
                        page: { margin: margins },
                        // For memo profile, suppress footer on the first page so page
                        // numbering effectively starts on page 2.
                        ...(profile === 'memo' ? { titlePage: true } : {}),
                    },
                    headers: header ? { default: header } : undefined,
                    footers: footer
                        ? {
                              default: footer,
                              ...(profile === 'memo'
                                  ? {
                                        first: new Footer({
                                            children: [],
                                        }),
                                    }
                                  : {}),
                          }
                        : undefined,
                    children: [...headerParagraphs, ...children] as (Paragraph | Table)[],
                },
            ],
        });
    }

    /**
     * Convert this processor's tokenised markdown to DOCX Paragraphs using the given style options.
     *
     * Used internally by `generateDocx` and externally by the per-message DOCX pipeline in
     * `generateExportContent`, which adds role headers and separators as explicit Paragraphs
     * rather than encoding them in the intermediate markdown string.
     */
    toDocxParagraphs(options: DocxGenerationOptions = {}): (Paragraph | Table)[] {
        return this.buildDocxParagraphs(options);
    }

    /**
     * Recursively convert marked inline tokens to docx TextRun objects.
     *
     * Why: marked's lexer splits inline content into typed tokens (text, strong, em,
     * codespan, br…). Passing `token.text` directly to a TextRun yields literal
     * markdown syntax characters instead of the intended formatting.
     */
    private static inlineTokensToRuns(
        tokens: any[],
        style: { bold?: boolean; italic?: boolean } = {},
        codeFont = 'Roboto Mono',
    ): TextRun[] {
        // marked's inline tokenizer HTML-entity-encodes text (same issue cleanCodespanTokens
        // addresses for codespans). Decode here so &quot; → " etc. in all TextRuns.
        const dt = (t: string) => EntityProcessor.decode(t || '');

        const runs: TextRun[] = [];
        for (const token of tokens) {
            switch (token.type) {
                case 'text':
                case 'escape':
                    if (Array.isArray(token.tokens) && token.tokens.length > 0) {
                        ContentProcessor.inlineTokensToRuns(token.tokens, style, codeFont)
                            .forEach((r) => runs.push(r));
                    } else {
                        runs.push(new TextRun({ text: dt(token.text), bold: style.bold, italics: style.italic }));
                    }
                    break;
                case 'strong':
                    ContentProcessor.inlineTokensToRuns(
                        token.tokens || [{ type: 'text', text: token.text }],
                        { ...style, bold: true },
                        codeFont,
                    ).forEach((r) => runs.push(r));
                    break;
                case 'em':
                    ContentProcessor.inlineTokensToRuns(
                        token.tokens || [{ type: 'text', text: token.text }],
                        { ...style, italic: true },
                        codeFont,
                    ).forEach((r) => runs.push(r));
                    break;
                case 'codespan':
                    // cleanCodespanTokens already decoded token.text; no double-decode needed.
                    runs.push(new TextRun({ text: token.text, font: codeFont, bold: style.bold, italics: style.italic }));
                    break;
                case 'inlineKatex':
                    // v1: monospace text run preserving raw LaTeX source
                    runs.push(new TextRun({ text: `$${token.text}$`, font: codeFont, bold: style.bold, italics: style.italic }));
                    break;
                case 'br':
                    runs.push(new TextRun({ break: 1 }));
                    break;
                case 'link':
                case 'del':
                    ContentProcessor.inlineTokensToRuns(
                        token.tokens || [{ type: 'text', text: token.text }],
                        style,
                        codeFont,
                    ).forEach((r) => runs.push(r));
                    break;
                default:
                    if (token.text) {
                        runs.push(new TextRun({ text: dt(token.text), bold: style.bold, italics: style.italic }));
                    }
            }
        }
        return runs;
    }

    // Converts the token AST to an array of DOCX Paragraphs.
    // Shared between generateDocx (via prebuiltChildren fallback) and toDocxParagraphs.
    private buildDocxParagraphs(options: DocxGenerationOptions = {}): (Paragraph | Table)[] {
        const profile: DocxProfile = options.profile ?? 'generic';
        const codeFont = profile === 'memo' ? 'Courier New' : 'Roboto Mono';
        const normalSizeHalfPoints = profile === 'memo' ? 24 : 22;
        const children: (Paragraph | Table)[] = [];

        this.tokens.forEach((token) => {
            if (token.type === 'hr') {
                children.push(
                    new Paragraph({
                        children: [new TextRun({ text: '' })],
                        border: {
                            bottom: {
                                color: 'AAAAAA',
                                style: BorderStyle.SINGLE,
                                size: 6,
                                space: 1,
                            },
                        },
                        spacing: { before: 120, after: 160 },
                    }),
                );
            } else if (token.type === 'heading') {
                const runs = Array.isArray((token as any).tokens) && (token as any).tokens.length > 0
                    ? ContentProcessor.inlineTokensToRuns((token as any).tokens, {}, codeFont)
                    : [new TextRun({ text: token.text })];
                children.push(
                    new Paragraph({
                        children: runs,
                        heading: ContentProcessor.mapHeadingLevel(token.depth),
                    }),
                );
            } else if (token.type === 'paragraph') {
                const runs = Array.isArray((token as any).tokens) && (token as any).tokens.length > 0
                    ? ContentProcessor.inlineTokensToRuns((token as any).tokens, {}, codeFont)
                    : [new TextRun({ text: token.text })];
                children.push(
                    new Paragraph({ children: runs }),
                );
            } else if (token.type === 'code') {
                const lines = String(token.text ?? '').split('\n');
                if (lines.length === 0) return;

                // Separate paragraphs per line to preserve line breaks in DOCX.
                lines.forEach((line, idx) => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: line,
                                    font: codeFont,
                                    size: normalSizeHalfPoints,
                                }),
                            ],
                            spacing: {
                                before: idx === 0 ? 120 : 0,
                                after: idx === lines.length - 1 ? 120 : 0,
                            },
                        }),
                    );
                });
            } else if (token.type === 'list') {
                const listParagraphs = ContentProcessor.buildDocxListParagraphs(token, codeFont);
                listParagraphs.forEach((p) => children.push(p));
            } else if (token.type === 'table') {
                children.push(ContentProcessor.buildDocxTable(token, codeFont));
            } else if (token.type === 'blockKatex') {
                // v1: monospace text run preserving raw LaTeX source ($$...$$)
                const mathLines = String((token as any).text ?? '').split('\n');
                children.push(
                    new Paragraph({
                        children: [
                            new TextRun({ text: '$$', font: codeFont }),
                            ...mathLines.map((line) => new TextRun({ text: line, font: codeFont, break: 1 })),
                            new TextRun({ text: '$$', font: codeFont, break: 1 }),
                        ],
                        spacing: { before: 120, after: 120 },
                    }),
                );
            }
            // TODO: Embed images in DOCX body paragraphs, similar to the PDF ImageRun pipeline.
        });

        return children;
    }

    /**
     * Generate plain text suitable for .txt export.
     * Reconstructs structure-preserving plain text (headings as #, lists as - / 1.,
     * code fenced, links as "text (url)") without TTS speech formatting.
     */
    generatePlainText(): string {
        return plainTextMarked.parser(this.tokens) as string;
    }

    // Helper to reconstruct list items from tokens for markdown export.
    private static reconstructList(listToken: any): string {
        if (!listToken.items) return '';

        const lines = listToken.items
            .map((item: any, index: number) => {
                const marker = listToken.ordered ? `${index + 1}. ` : '- ';
                return `${marker}${item.text}`;
            })
            .join('\n');
        return `${lines}\n`;
    }

    private static mapHeadingLevel(depth: number) {
        switch (depth) {
            case 1:
                return HeadingLevel.HEADING_1;
            case 2:
                return HeadingLevel.HEADING_2;
            default:
                return HeadingLevel.HEADING_3;
        }
    }

    /**
     * Convert a marked list token into docx Paragraphs.
     *
     * Inputs: marked "list" token with `.ordered` + `.items[]` (each item has `.text` and `.tokens`)
     * Output: array of Paragraphs with bullet formatting or explicit numbering prefixes.
     */
    private static buildDocxListParagraphs(listToken: any, codeFont = 'Roboto Mono'): Paragraph[] {
        if (!listToken?.items || !Array.isArray(listToken.items)) return [];

        const ordered = !!listToken.ordered;
        return listToken.items.map((item: any, index: number) => {
            // marked wraps list item content in a block-level text token; its sub-tokens
            // carry the inline formatting (strong, em, codespan…).
            const inlineTokens: any[] | null =
                Array.isArray(item.tokens) && item.tokens.length > 0 && Array.isArray(item.tokens[0].tokens)
                    ? item.tokens[0].tokens
                    : null;

            const fallbackText = String(item?.text ?? '').trim();
            if (!fallbackText && !inlineTokens) return new Paragraph('');

            const runs = inlineTokens
                ? ContentProcessor.inlineTokensToRuns(inlineTokens, {}, codeFont)
                : [new TextRun({ text: fallbackText })];

            if (ordered) {
                // Explicit numbering prefix avoids managing docx numbering definitions.
                return new Paragraph({
                    children: [new TextRun({ text: `${index + 1}. ` }), ...runs],
                });
            }

            return new Paragraph({
                children: runs,
                bullet: { level: 0 },
            });
        });
    }

    private static buildDocxTable(token: any, codeFont = 'Roboto Mono'): Table {
        // Page width minus 1" margins on each side = 12240 - 2*1440 = 9360 DXA
        const PAGE_WIDTH_DXA = 9360;
        const colCount = token.header?.length ?? 1;
        const colWidthDXA = Math.floor(PAGE_WIDTH_DXA / colCount);

        const headerRow = new TableRow({
            tableHeader: true,
            children: (token.header as any[]).map((cell: any) => {
                const runs = Array.isArray(cell.tokens) && cell.tokens.length > 0
                    ? ContentProcessor.inlineTokensToRuns(cell.tokens, { bold: true }, codeFont)
                    : [new TextRun({ text: String(cell.text ?? ''), bold: true })];
                return new TableCell({
                    width: { size: colWidthDXA, type: WidthType.DXA },
                    children: [new Paragraph({ children: runs })],
                });
            }),
        });

        const bodyRows = (token.rows as any[][]).map((row: any[]) =>
            new TableRow({
                children: row.map((cell: any) => {
                    const runs = Array.isArray(cell.tokens) && cell.tokens.length > 0
                        ? ContentProcessor.inlineTokensToRuns(cell.tokens, {}, codeFont)
                        : [new TextRun({ text: String(cell.text ?? '') })];
                    return new TableCell({
                        width: { size: colWidthDXA, type: WidthType.DXA },
                        children: [new Paragraph({ children: runs })],
                    });
                }),
            }),
        );

        return new Table({ rows: [headerRow, ...bodyRows] });
    }
}
